<template>
  <div class="lk-notifications-admin">
    <div class="lk-notifications-admin__header">
      <div class="lk-notifications-admin__header-left">
        <v-icon color="primary" class="mr-3">mdi-bell-plus-outline</v-icon>
        <div>
          <h2 class="lk-notifications-admin__title">Уведомления личного кабинета</h2>
          <p class="lk-notifications-admin__subtitle mb-0">
            Публикация сообщений в колокольчике для пользователей ЛК
          </p>
        </div>
      </div>
      <div class="lk-notifications-admin__header-actions">
        <v-btn
          color="error"
          outlined
          rounded
          class="text-none"
          @click="broadcastsDialogOpen = true"
        >
          <v-icon left>mdi-delete-sweep</v-icon>
          Удаление уведомлений
        </v-btn>
        <v-btn
          color="success"
          rounded
          class="text-none"
          :loading="sending"
          :disabled="!canSubmit"
          @click="submit"
        >
          <v-icon left>mdi-send</v-icon>
          Опубликовать
        </v-btn>
      </div>
    </div>

    <lk-notifications-broadcasts-dialog
      v-model="broadcastsDialogOpen"
      @deleted="onBroadcastsChanged"
    />

    <v-form ref="form" v-model="valid">
      <v-row align="start">
        <v-col cols="12" lg="7">
          <v-card outlined class="lk-notifications-admin__card">
            <v-card-title class="lk-notifications-admin__card-title">
              <v-icon left small color="primary">mdi-message-text-outline</v-icon>
              Сообщение
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="form.title"
                label="Заголовок"
                outlined
                dense
                :rules="[v => !!v && v.trim().length > 0 || 'Укажите заголовок']"
                maxlength="255"
                counter="255"
                prepend-inner-icon="mdi-format-title"
              />
              <v-textarea
                v-model="form.body"
                label="Текст уведомления"
                hint="Enter — новая строка (абзац)"
                persistent-hint
                outlined
                dense
                rows="5"
                auto-grow
                :rules="[v => !!v && v.trim().length > 0 || 'Укажите текст']"
                prepend-inner-icon="mdi-text"
              />
              <v-autocomplete
                v-model="form.type"
                :items="typeOptions"
                item-text="text"
                item-value="value"
                label="Тип уведомления"
                hint="Определяет иконку в колокольчике"
                persistent-hint
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-tag-outline"
                no-data-text="Нет типов"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small :color="item.iconColor" class="mr-2">{{ item.icon }}</v-icon>
                    <span>{{ item.text }}</span>
                  </div>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on" class="lk-notifications-admin__type-item">
                    <v-list-item-avatar
                      class="my-2 lk-notifications-admin__type-avatar"
                      size="36"
                      :style="{ background: item.iconColor }"
                    >
                      <v-icon small color="white">{{ item.icon }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ item.text }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                      <v-list-item-subtitle class="caption grey--text">
                        Код: {{ item.value }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <v-card outlined class="lk-notifications-admin__card mb-4">
            <v-card-title class="lk-notifications-admin__card-title">
              <v-icon left small color="primary">mdi-account-multiple-outline</v-icon>
              Получатели
            </v-card-title>
            <v-card-text>
              <v-switch
                v-model="sendToAll"
                label="Всем пользователям ЛК"
                color="primary"
                hide-details
                class="mt-0"
              />
              <v-alert
                v-if="sendToAll"
                type="info"
                dense
                outlined
                class="mt-4 mb-0"
              >
                Уведомление получат все пользователи ЛК
              </v-alert>

              <v-expand-transition>
                <div v-if="!sendToAll" class="mt-4">
                  <v-autocomplete
                    v-model="form.roles"
                    :items="roleOptions"
                    item-text="text"
                    item-value="value"
                    label="Роли получателей"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                    prepend-inner-icon="mdi-account-badge-outline"
                    placeholder="Выберите роли"
                    no-data-text="Нет ролей"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon class="mr-3">
                          <v-icon color="primary">mdi-shield-account-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.text }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>

                  <v-autocomplete
                    v-model="form.userIds"
                    :items="lkUsers"
                    :loading="usersLoading"
                    item-text="displayName"
                    item-value="id"
                    label="Пользователи ЛК"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-account-outline"
                    no-data-text="Нет пользователей"
                    placeholder="Выберите пользователей"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon class="mr-3">
                          <v-icon color="grey darken-1">mdi-account-circle-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                          <v-list-item-subtitle v-if="item.roleLabel" class="caption">
                            {{ item.roleLabel }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>

                  <v-alert
                    v-if="!form.roles.length && !form.userIds.length"
                    type="warning"
                    dense
                    outlined
                    class="mt-4 mb-0"
                  >
                    Укажите хотя бы одну роль или пользователя
                  </v-alert>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-card outlined class="lk-notifications-admin__card lk-notifications-admin__preview">
            <v-card-title class="lk-notifications-admin__card-title d-flex flex-wrap">
              <div class="d-flex align-center">
                <v-icon left small color="primary">mdi-eye-outline</v-icon>
                Предпросмотр
              </div>
              <v-chip x-small class="ml-2" color="grey lighten-3">
                как в колокольчике шапки ЛК
              </v-chip>
            </v-card-title>
            <v-card-text class="pt-0 pb-4">
              <v-row>
                <v-col cols="12" md="6" lg="5" xl="4">
                  <div class="lk-notifications-admin__preview-frame">
                    <div class="lk-notifications-admin__preview-panel">
                      <div class="lk-notifications-admin__preview-panel-header">
                        <div class="d-flex align-center">
                          <v-icon color="#FFC107" size="20" class="mr-2">mdi-bell-ring-outline</v-icon>
                          <span class="lk-notifications-admin__preview-panel-title">Уведомления</span>
                          <v-chip
                            x-small
                            color="error"
                            text-color="white"
                            class="ml-2 font-weight-medium"
                          >
                            1
                          </v-chip>
                        </div>
                        <v-btn
                          x-small
                          rounded
                          outlined
                          color="primary"
                          class="text-none lk-notifications-admin__preview-read-btn"
                          disabled
                        >
                          <v-icon left x-small>mdi-check-all</v-icon>
                          Прочитать все
                        </v-btn>
                      </div>
                      <v-divider />
                      <div class="lk-notifications-admin__preview-section-label">
                        <v-icon x-small class="mr-1">mdi-circle</v-icon>
                        Новые
                      </div>
                      <div
                        class="lk-notifications-admin__preview-card"
                        :class="{ 'lk-notifications-admin__preview-card--placeholder': !hasPreviewContent }"
                        :style="previewUnreadCardStyle"
                      >
                        <div class="lk-notifications-admin__preview-notification">
                          <div
                            class="lk-notifications-admin__preview-avatar"
                            :style="previewAvatarStyle"
                          >
                            <v-icon small :color="previewTypeColor">{{ previewTypeIcon }}</v-icon>
                          </div>
                          <div class="lk-notifications-admin__preview-body">
                            <div class="lk-notifications-admin__preview-top">
                              <span
                                class="lk-notifications-admin__preview-item-title"
                                :class="{ 'lk-notifications-admin__preview-item-title--placeholder': !form.title.trim() }"
                              >
                                {{ form.title.trim() || 'Заголовок уведомления' }}
                              </span>
                              <span
                                class="lk-notifications-admin__preview-dot"
                                :style="{ background: previewTypeColor }"
                              />
                            </div>
                            <v-chip
                              v-if="selectedTypeOption"
                              x-small
                              label
                              outlined
                              :color="selectedTypeOption.chipColor"
                              class="lk-notifications-admin__preview-type-chip mt-1"
                            >
                              <v-icon left x-small>{{ selectedTypeOption.icon }}</v-icon>
                              {{ selectedTypeOption.text }}
                            </v-chip>
                            <p
                              class="lk-notifications-admin__preview-text mb-0"
                              :class="{ 'lk-notifications-admin__preview-text--placeholder': !form.body.trim() }"
                            >
                              {{ previewBodyText }}
                            </p>
                            <span class="lk-notifications-admin__preview-date">Сейчас</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6" lg="7" xl="8" class="d-flex align-center">
                  <div class="lk-notifications-admin__preview-side">
                    <p v-if="!hasPreviewContent" class="lk-notifications-admin__preview-hint mb-3">
                      <v-icon small color="info" class="mr-1">mdi-information-outline</v-icon>
                      Заполните заголовок и текст слева — предпросмотр обновится автоматически
                    </p>
                    <ul class="lk-notifications-admin__preview-notes mb-0">
                      <li>Так уведомление откроется по клику на жёлтый колокольчик в шапке</li>
                      <li>Красная точка — непрочитанное; тип отображается чипом с подписью</li>
                      <li>После публикации пользователи увидят карточку в блоке «Новые»</li>
                    </ul>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <v-alert
      v-if="lastResult"
      type="success"
      dense
      outlined
      class="mt-4"
    >
      <v-icon left small>mdi-check-circle</v-icon>
      Уведомление отправлено. Получателей: {{ lastResult.recipients_count }}.
    </v-alert>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import LkNotificationsBroadcastsDialog from '@/components/adminSettings/LkNotificationsBroadcastsDialog.vue';
import {
  LK_NOTIFICATION_ROLE_OPTIONS,
  LK_NOTIFICATION_ROLES,
  LK_NOTIFICATION_TYPE_OPTIONS,
  getNotificationTypeAvatarStyle,
  getNotificationTypeColor,
  getNotificationTypeIcon,
  getNotificationTypeOption,
  getNotificationTypeUnreadStyle,
} from '@/constants/lk-notifications';

const ROLE_LABEL_BY_VALUE = LK_NOTIFICATION_ROLE_OPTIONS.reduce((acc, role) => {
  acc[role.value] = role.text;
  return acc;
}, {});

export default {
  name: 'LkNotificationsAdmin',
  components: { LkNotificationsBroadcastsDialog },
  data () {
    return {
      valid: false,
      sending: false,
      usersLoading: false,
      sendToAll: true,
      broadcastsDialogOpen: false,
      lastResult: null,
      roleOptions: LK_NOTIFICATION_ROLE_OPTIONS,
      typeOptions: LK_NOTIFICATION_TYPE_OPTIONS,
      form: {
        title: '',
        body: '',
        type: null,
        roles: [],
        userIds: [],
      },
      lkUsers: [],
    };
  },
  computed: {
    canSubmit () {
      if (!this.valid) return false;
      if (this.sendToAll) return true;
      return this.form.roles.length > 0 || this.form.userIds.length > 0;
    },
    selectedTypeOption () {
      return getNotificationTypeOption(this.form.type);
    },
    previewTypeIcon () {
      return getNotificationTypeIcon(this.form.type);
    },
    previewTypeColor () {
      return getNotificationTypeColor(this.form.type);
    },
    previewAvatarStyle () {
      return getNotificationTypeAvatarStyle(this.form.type);
    },
    previewUnreadCardStyle () {
      if (!this.hasPreviewContent) {
        return {};
      }
      return getNotificationTypeUnreadStyle(this.form.type);
    },
    hasPreviewContent () {
      return !!(this.form.title.trim() || this.form.body.trim());
    },
    previewBodyText () {
      return this.form.body.trim()
        ? this.form.body
        : 'Текст уведомления появится здесь после ввода';
    },
  },
  created () {
    this.loadLkUsers();
  },
  methods: {
    ...mapActions({
      createNotification: 'LkNotifications/create',
      fetchUnreadCount: 'LkNotifications/fetchUnreadCount',
      loadUsers: 'User/loadUsers',
    }),
    onBroadcastsChanged () {
      this.fetchUnreadCount();
    },
    normalizeBody (text) {
      return text.replace(/\r\n/g, '\n').replace(/^\s+|\s+$/g, '');
    },
    loadLkUsers () {
      this.usersLoading = true;
      this.loadUsers({})
        .then(() => {
          this.lkUsers = this.$store.getters['User/users']
            .filter(user => {
              const roleKey = user.role?.name;
              return roleKey && LK_NOTIFICATION_ROLES.includes(roleKey) && !user.blocked;
            })
            .map(user => ({
              ...user,
              displayName: `${user.name} (${user.email})`,
              roleLabel: ROLE_LABEL_BY_VALUE[user.role?.name] || user.role?.name,
            }));
        })
        .catch(() => {
          this.$flash_warning('Не удалось загрузить список пользователей');
        })
        .finally(() => {
          this.usersLoading = false;
        });
    },
    buildPayload () {
      const payload = {
        title: this.form.title.trim(),
        body: this.normalizeBody(this.form.body),
      };
      if (this.form.type) {
        payload.type = this.form.type;
      }
      if (!this.sendToAll) {
        payload.recipients = {};
        if (this.form.roles.length) {
          payload.recipients.roles = [...this.form.roles];
        }
        if (this.form.userIds.length) {
          payload.recipients.user_ids = [...this.form.userIds];
        }
      }
      return payload;
    },
    submit () {
      if (!this.$refs.form.validate() || !this.canSubmit) {
        return;
      }
      this.sending = true;
      this.lastResult = null;
      this.createNotification(this.buildPayload())
        .then(({ data }) => {
          this.lastResult = data.data;
          this.$flash_success('Уведомление опубликовано');
          this.form.title = '';
          this.form.body = '';
          this.form.type = null;
          this.form.roles = [];
          this.form.userIds = [];
          this.sendToAll = true;
          this.$refs.form.resetValidation();
        })
        .catch((err) => {
          const message = err?.response?.data?.message || err?.response?.data?.errors || 'Не удалось опубликовать уведомление';
          if (typeof message === 'object') {
            Object.values(message).flat().forEach(msg => this.$flash_warning(String(msg)));
          } else {
            this.$flash_warning(String(message));
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.lk-notifications-admin {
  padding-bottom: 16px;
}

.lk-notifications-admin__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.lk-notifications-admin__header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.lk-notifications-admin__header-left {
  display: flex;
  align-items: flex-start;
}

.lk-notifications-admin__title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #37474f;
  line-height: 1.3;
}

.lk-notifications-admin__subtitle {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.54);
  margin-top: 4px;
}

.lk-notifications-admin__card {
  border-radius: 12px;
}

.lk-notifications-admin__card-title {
  font-size: 1rem;
  font-weight: 500;
  padding-bottom: 8px;
}

.lk-notifications-admin__type-item {
  min-height: 64px;
}

.lk-notifications-admin__preview {
  background: #f8f9fb;
  border: 2px solid rgba(13, 64, 146, 0.12) !important;
}

.lk-notifications-admin__preview-frame {
  padding: 16px;
  min-height: 220px;
  background: linear-gradient(145deg, #eceff1 0%, #cfd8dc 100%);
  border-radius: 12px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
}

.lk-notifications-admin__preview-side {
  width: 100%;
  padding: 8px 0 8px 8px;
}

.lk-notifications-admin__preview-notes {
  padding-left: 20px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  line-height: 1.6;

  li + li {
    margin-top: 8px;
  }
}

.lk-notifications-admin__preview-panel {
  max-width: 100%;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.lk-notifications-admin__preview-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(255, 193, 7, 0.18) 0%, rgba(255, 193, 7, 0.04) 100%);
}

.lk-notifications-admin__preview-panel-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #263238;
}

.lk-notifications-admin__preview-read-btn {
  font-size: 0.7rem !important;
  opacity: 0.85;
}

.lk-notifications-admin__preview-section-label {
  display: flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.5);
  padding: 10px 14px 6px;
  background: #fafafa;

  .v-icon {
    color: #f44336;
  }
}

.lk-notifications-admin__preview-card {
  padding: 12px 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  transition: background 0.25s ease, border-color 0.25s ease;

  &--placeholder {
    background: rgba(255, 193, 7, 0.04) !important;
    border-left: none !important;
  }
}

.lk-notifications-admin__preview-notification {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.lk-notifications-admin__type-avatar {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.lk-notifications-admin__preview-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.lk-notifications-admin__preview-body {
  flex: 1;
  min-width: 0;
}

.lk-notifications-admin__preview-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.lk-notifications-admin__preview-item-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #263238;
  line-height: 1.35;
  word-break: break-word;

  &--placeholder {
    color: rgba(0, 0, 0, 0.38);
    font-weight: 500;
    font-style: italic;
  }
}

.lk-notifications-admin__preview-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
  transition: background 0.2s ease;
}

.lk-notifications-admin__preview-type-chip {
  height: 20px !important;
  font-size: 0.65rem !important;
}

.lk-notifications-admin__preview-text {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.45;
  margin-top: 6px;
  word-break: break-word;
  white-space: pre-line;

  &--placeholder {
    color: rgba(0, 0, 0, 0.38);
    font-style: italic;
  }
}

.lk-notifications-admin__preview-date {
  display: block;
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 6px;
}

.lk-notifications-admin__preview-hint {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.55);
}

@media screen and (max-width: 1264px) {
  .lk-notifications-admin__header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
